import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import HeaderPublic from './HeaderPublic';
import { Col, Container, Row } from 'react-bootstrap';
import { firestore } from '../Firebase';
import { serverTimestamp } from 'firebase/firestore';
import DynamicBoxOutput from './DynamicBoxOutput';
import QuestionBox from './QuestionBox';
import Chat from './Chat';
import EmojiReactions from './EmojiReactions';
import { AuthContextPublic } from '../context/AuthContextPublic';

const VimeoVideo = React.memo(({ vimeoID, vimeoKey, vimeoType }) => {
  const videoSrc =
    vimeoType === 'upload'
      ? `https://player.vimeo.com/video/${vimeoID}?h=${vimeoKey}&badge=0&autopause=0&player_id=0&app_id=58479`
      : `https://vimeo.com/event/${vimeoID}/embed/${vimeoKey}`;

  return (
    <iframe
      title="vimeoVideo"
      src={videoSrc}
      frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      }}
    ></iframe>
  );
});

const Stream = () => {
  const { videoID: paramVideoID } = useParams();
  const navigate = useNavigate();
  const [videoData, setVideoData] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [dynamicBoxContent, setDynamicBoxContent] = useState('');
  const [dynamicBoxLoading, setDynamicBoxLoading] = useState(false);
  const { userID } = useContext(AuthContextPublic) || {};

  // Consolidated feature flags
  const [features, setFeatures] = useState({
    hasBackgroundImage: false,
    hasLightSubtitle: false,
    hasLogo: false,
    hasDynamicBox: false,
    hasChat: false,
    hasQuestionBox: false,
    hasAnonymousQuestions: false,
    hasBannerLogo: false,
    hasDarkModeOn: false,
    hasEmojiReactions: true, // Default to true for emoji reactions
  });

  // Consolidated customization values
  const [customization, setCustomization] = useState({
    subtitle: '',
    logoPath: '',
    bgPath: '',
    hex1: '',
    hex2: '',
    bannerPath: '',
  });

  useEffect(() => {
    // Combine feature and customization listeners into one
    const unsubscribeFeatures = firestore
      .collection('settings')
      .doc('features')
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            setFeatures({
              hasBackgroundImage: data.hasBackgroundImage || false,
              hasLightSubtitle: data.hasLightSubtitle || false,
              hasLogo: data.hasLogo || false,
              hasDynamicBox: data.hasDynamicBox || false,
              hasChat: data.hasChat || false,
              hasQuestionBox: data.hasQuestionBox || false,
              hasAnonymousQuestions: data.hasAnonymousQuestions || false,
              hasBannerLogo: data.hasBannerLogo || false,
              hasDarkModeOn: data.hasDarkModeOn || false,
              hasEmojiReactions: data.hasEmojiReactions !== false, // Default to true if not specified
            });
          }
        },
        (error) => {
          console.error('Error listening to features changes:', error);
        }
      );

    const unsubscribeCustomization = firestore
      .collection('settings')
      .doc('customization')
      .onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            setCustomization({
              subtitle: data.subtitle || '',
              logoPath: data.logoPath || '',
              bgPath: data.bgPath || '',
              singleVideoId: data.singleVideoId || '',
              hex1: data.hex1 || '',
              hex2: data.hex2 || '',
              bannerPath: data.bannerPath || '',
            });
          } else {
            setCustomization((prevCustomization) => ({
              ...prevCustomization,
              singleVideoId: '',
            }));
          }
          setSettingsLoaded(true);
        },
        (error) => {
          console.error('Error listening to customization changes:', error);
        }
      );

    return () => {
      unsubscribeFeatures();
      unsubscribeCustomization();
    };
  }, []);

  useEffect(() => {
    if (settingsLoaded && !paramVideoID && customization.singleVideoId) {
      navigate(`/stream/${customization.singleVideoId}`, { replace: true });
    }
  }, [settingsLoaded, paramVideoID, customization.singleVideoId, navigate]);

  useEffect(() => {
    if (!settingsLoaded) return () => {};

    const videoID = paramVideoID || customization.singleVideoId;
    if (!videoID) {
      setVideoData(null);
      setVideoLoading(false);
      return () => {};
    }

    setVideoLoading(true);
    const unsubscribeVideoData = firestore
      .collection('videos')
      .doc(videoID)
      .onSnapshot(
        (doc) => {
          setVideoData(doc.exists ? { id: doc.id, ...doc.data() } : null);
          setVideoLoading(false);
          if (!doc.exists) {
            console.error('No such video found.');
          }
        },
        (error) => {
          console.error('Error fetching video:', error);
          setVideoData(null);
          setVideoLoading(false);
        }
      );

    return () => {
      unsubscribeVideoData();
    };
  }, [paramVideoID, customization.singleVideoId, settingsLoaded]);

  useEffect(() => {
    if (!videoData) {
      setDynamicBoxContent('');
      setDynamicBoxLoading(false);
      return () => {};
    }

    if (!videoData.activeSlideId) {
      setDynamicBoxContent(videoData.dynamicBox || '');
      setDynamicBoxLoading(false);
      return () => {};
    }

    setDynamicBoxLoading(true);
    const unsubscribeDynamicBox = firestore
      .collection('videos')
      .doc(videoData.id)
      .collection('dynamicbox')
      .doc(videoData.activeSlideId)
      .onSnapshot(
        (doc) => {
          setDynamicBoxContent(
            doc.exists ? doc.data().content || '' : videoData.dynamicBox || ''
          );
          setDynamicBoxLoading(false);
        },
        (error) => {
          console.error('Error listening to dynamic box slide:', error);
          setDynamicBoxContent(videoData.dynamicBox || '');
          setDynamicBoxLoading(false);
        }
      );

    return () => unsubscribeDynamicBox();
  }, [videoData]);

  useEffect(() => {
    if (!userID) return () => {};

    const viewerDocRef = firestore.collection('viewers').doc(userID);
    const heartbeatData = () => ({
      timestamp: serverTimestamp(),
      lastUpdated: new Date().getTime(),
    });

    // Only update an existing viewer session. Do not use set(..., { merge: true })
    // here: that can create blank session docs if Stream mounts before the full
    // login/session write finishes or if an authenticated viewer reaches Stream
    // without completing login metadata capture.
    const updateActivity = (label) => {
      viewerDocRef.update(heartbeatData()).catch((error) => {
        if (error?.code === 'not-found') {
          console.warn(
            `Skipping ${label}: viewer session document does not exist yet`,
            { userID }
          );
          return;
        }
        console.error(`Error ${label}:`, error);
      });
    };

    updateActivity('adding activity');

    // Update every minute
    const interval = setInterval(() => {
      updateActivity('updating activity');
    }, 60000);

    return () => {
      clearInterval(interval);
      if (userID) {
        updateActivity('marking interrupted');
      }
    };
  }, [userID]);

  const targetVideoID = paramVideoID || customization.singleVideoId;
  const hasVisibleDynamicBox =
    features.hasDynamicBox &&
    !dynamicBoxLoading &&
    dynamicBoxContent.trim().length > 0;
  const hasSidePanel =
    hasVisibleDynamicBox || features.hasQuestionBox || features.hasChat;

  if (!settingsLoaded || videoLoading) {
    return <div>Loading...</div>;
  }

  // Ensure hex values have # prefix if they don't already
  const hex1 = customization.hex1
    ? customization.hex1.startsWith('#')
      ? customization.hex1
      : `#${customization.hex1}`
    : '#ffffff';

  const hex2 = customization.hex2
    ? customization.hex2.startsWith('#')
      ? customization.hex2
      : `#${customization.hex2}`
    : '#ffffff';

  // Create background style directly
  const containerStyle = {
    minHeight: '100vh',
    position: 'relative',
    zIndex: 1,
  };

  const background = features.hasBackgroundImage && customization.bgPath ? (
    <img
      src={customization.bgPath}
      alt=""
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center',
        zIndex: -1,
      }}
    />
  ) : (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        background: `linear-gradient(to right, ${hex1}, ${hex2})`,
        zIndex: -1,
      }}
    ></div>
  );

  if (!videoData) {
    return (
      <>
        {background}
        <Container fluid style={containerStyle}>
          <Row>
            <HeaderPublic features={features} />
          </Row>
          <Row className="mx-1 justify-content-center">
            <Col lg={8} className="mt-5">
              <div className="alert alert-info text-center" role="alert">
                <h3 className="mb-2">
                  {targetVideoID
                    ? 'Selected Video Not Found'
                    : 'No Single Video Selected'}
                </h3>
                <p className="mb-0">
                  {targetVideoID
                    ? 'The selected single video is missing or unavailable. Choose a valid Single Video in Settings.'
                    : 'Choose a Single Video in Settings or turn on Conference Mode.'}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      {background}
      <Container fluid style={containerStyle}>
        <Row>
          <HeaderPublic features={features} />
        </Row>
        <Row className="mx-1">
          {hasSidePanel ? null : (
            <Col lg={2} md={1}></Col>
          )}
          {features.hasBannerLogo ? (
            <Col>
              <img src={customization.bannerPath} alt="Banner" height="80" />
            </Col>
          ) : (
            <Col>
              {features.hasLightSubtitle ? (
                <div className="h1" style={{ color: 'white' }}>
                  {videoData.title || 'Stream'}
                </div>
              ) : (
                <div className="h1">{videoData.title}</div>
              )}
            </Col>
          )}
        </Row>
        <Row className="mx-1">
          {hasSidePanel ? null : (
            <Col lg={2} md={1}></Col>
          )}

          <Col lg={hasSidePanel ? 9 : 8} md={hasSidePanel ? 9 : 10}>
            {videoData.bannerMessage && (
              <div className="alert alert-info" role="alert">
                {videoData.bannerMessage}
              </div>
            )}
            {videoData && (
              <div className="video-container mb-4">
                {videoData.vimeoID && videoData.vimeoKey ? (
                  <VimeoVideo
                    vimeoID={videoData.vimeoID}
                    vimeoKey={videoData.vimeoKey}
                    vimeoType={videoData.vimeoType || 'event'} // Default to 'event' if not specified
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center h-100 bg-light rounded-3 shadow-sm p-5 text-muted"
                    style={{ minHeight: '400px' }}
                  >
                    <div className="text-center">
                      <h3>No Video Available</h3>
                      <p>The stream will begin shortly.</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {features.hasEmojiReactions && (
              <EmojiReactions features={features} />
            )}
          </Col>

          {hasSidePanel ? (
            <Col lg={3} md={3}>
              <Row>
                {hasVisibleDynamicBox && (
                  <Col
                    className={`p-3 ${
                      features.hasDarkModeOn ? 'bg-dark' : 'bg-white'
                    } rounded-3 shadow w-100 mb-3`}
                  >
                    <DynamicBoxOutput content={dynamicBoxContent} />
                  </Col>
                )}
              </Row>
              <Row>
                {features.hasQuestionBox && (
                  <Col
                    className={`px-2 pt-1 pb-2 ${
                      features.hasDarkModeOn ? 'bg-dark' : 'bg-white'
                    } rounded-3 shadow w-100 mb-3`}
                  >
                    <QuestionBox features={features} />
                  </Col>
                )}
              </Row>
              <Row>
                {features.hasChat && (
                  <Col
                    className={`p-1 ${
                      features.hasDarkModeOn ? 'bg-dark' : 'bg-white'
                    } rounded-3 shadow w-100`}
                  >
                    <Chat features={features} />
                  </Col>
                )}
              </Row>
            </Col>
          ) : (
            <Col md={2}></Col>
          )}
        </Row>
      </Container>
    </>
  );
};

export default Stream;
